import { ACTIVITY_LABELS, ROOM_FUNCTION_LABELS, type AgentContext, type AgentMessage, type AgentToolResultBlock, type AgentToolUseBlock, type EnvironmentReading, type TaskPlan } from "@calcplan/ai-capabilities";
import { withoutToolNarration } from "./ai-response";
import { ensureToolResultProtocol } from "./ai-tool-protocol";

export type IndependentReviewBrief = NonNullable<NonNullable<AgentContext["orchestration"]>["independentReview"]>;

export type IndependentVisualReviewScope = {
  context: "targets" | "environment";
  captureId: string;
  coveredIds: string[];
  fittedIds: string[];
};

function latestCaptureResult(rawMessages: AgentMessage[]): AgentToolResultBlock | null {
  const messages = withoutToolNarration(rawMessages);
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    for (const block of messages[messageIndex]!.content) {
      if (block.type !== "tool_result" || !block.result || typeof block.result !== "object") continue;
      if (typeof (block.result as Record<string, unknown>).captureId === "string") return block;
    }
  }
  return null;
}

/**
 * Descreve somente o enquadramento que o crítico independente realmente
 * recebeu. Uma captura local não pode reabrir decisões de outros passos que
 * ficaram fora desta imagem; a panorâmica final, por outro lado, continua
 * julgando o conjunto inteiro.
 */
export function independentVisualReviewScope(rawMessages: AgentMessage[]): IndependentVisualReviewScope | null {
  const capture = latestCaptureResult(rawMessages);
  if (!capture?.result || typeof capture.result !== "object") return null;
  const result = capture.result as Record<string, unknown>;
  const visualReview = result.visualReview && typeof result.visualReview === "object"
    ? result.visualReview as Record<string, unknown>
    : {};
  const strings = (value: unknown): string[] => Array.isArray(value)
    ? [...new Set(value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0))]
    : [];
  return {
    context: result.context === "environment" ? "environment" : "targets",
    captureId: String(result.captureId),
    coveredIds: strings(visualReview.coveredIds ?? result.coveredIds),
    fittedIds: strings(result.fittedIds ?? result.targetIds),
  };
}

/**
 * O contexto do ambiente que o crítico recebe: as linhas que as paredes já
 * têm, a função do cômodo, as superfícies e o alcance — os mesmos fatos do
 * planejador, nunca a alternativa nem os valores que ele escolheu. As
 * premissas declaradas no plano vão junto para serem confirmadas ou refutadas.
 */
export function environmentContextForReview(
  reading: EnvironmentReading,
  assumptions: NonNullable<TaskPlan["assumptions"]>,
  datumDecisions: NonNullable<NonNullable<IndependentReviewBrief["environmentContext"]>["datumDecisions"]> = [],
): NonNullable<IndependentReviewBrief["environmentContext"]> {
  return {
    datumDecisions: datumDecisions.slice(0, 16),
    roomFunction: {
      value: `${reading.roomFunction.value} (${ROOM_FUNCTION_LABELS[reading.roomFunction.value]})`,
      confidence: reading.roomFunction.confidence,
      evidence: reading.roomFunction.evidence.slice(0, 8),
    },
    ceilingHeightMm: reading.ceilingHeightMm,
    userProfile: {
      label: reading.userProfile.label,
      reach: {
        primeMm: reading.userProfile.reach.primeMm,
        acceptableMm: reading.userProfile.reach.acceptableMm,
        headZoneMm: reading.userProfile.reach.headZoneMm,
        storageTopMaxMm: reading.userProfile.reach.storageTopMaxMm,
      },
    },
    walls: reading.walls
      .filter((wall) => wall.elementIds.length > 0 || wall.lines.some((line) => line.family === "opening"))
      .slice(0, 16)
      .map((wall) => ({
        wallId: wall.wallId,
        lines: wall.lines.slice(0, 24).map((line) => ({
          heightMm: line.heightMm,
          kind: line.kind,
          family: line.family,
          ...(line.sourceName ? { sourceName: line.sourceName } : {}),
        })),
      })),
    elements: reading.elements.slice(0, 60).map((element) => ({
      name: element.name,
      typology: element.typology.label,
      suspended: element.suspended,
      bottomMm: element.bottomMm,
      topMm: element.topMm,
      wallId: element.wallId,
    })),
    surfaces: reading.surfaces.slice(0, 40).map((surface) => ({
      name: surface.name,
      topMm: surface.topMm,
      activity: `${surface.activity} (${ACTIVITY_LABELS[surface.activity]})`,
      confidence: surface.confidence,
    })),
    assumptions: assumptions.slice(0, 8),
  };
}

export function buildIndependentReviewBrief(input: {
  objective: string;
  plan: TaskPlan;
  changedElements: IndependentReviewBrief["changedElements"];
  activeStepId?: string | null;
  environment?: EnvironmentReading | null;
  reviewHistory?: IndependentReviewBrief["reviewHistory"];
}): IndependentReviewBrief {
  const activeStep = input.activeStepId
    ? input.plan.steps.find((step) => step.id === input.activeStepId) ?? null
    : null;
  const localReview = Boolean(activeStep);
  const localSubjects = new Set([
    ...input.changedElements.map((element) => element.name),
    ...input.plan.designResolutions
      .filter((resolution) => !activeStep || resolution.stepId === activeStep.id)
      .map((resolution) => resolution.subject),
  ]);
  const appliesToLocalScope = (subject: string): boolean => !localReview
    || [...localSubjects].some((candidate) => subjectsOverlap(subject, candidate));
  const explicitRequirements = input.plan.decisionAuthorities
    .filter((decision) => decision.authority === "explicit")
    .filter((decision) => appliesToLocalScope(decision.subject))
    .map((decision) => ({
      subject: decision.subject,
      attribute: decision.attribute,
      requirement: decision.requirement,
    }));
  for (const resolution of input.plan.designResolutions) {
    if (activeStep && resolution.stepId !== activeStep.id) continue;
    if (resolution.authority !== "explicit") continue;
    if (explicitRequirements.some((candidate) => (
      candidate.subject === resolution.subject
      && candidate.attribute === resolution.attribute
      && candidate.requirement === resolution.decision
    ))) continue;
    explicitRequirements.push({
      subject: resolution.subject,
      attribute: resolution.attribute,
      requirement: resolution.decision,
    });
  }
  const currentDeliverable = activeStep
    ? `${activeStep.title}: ${activeStep.deliverable}`
    : undefined;
  const deferredDeliverables = activeStep
    ? input.plan.steps
        .filter((step) => step.id !== activeStep.id && step.status !== "completed")
        .map((step) => `${step.title}: ${step.deliverable}`)
    : [];
  return {
    originalObjective: activeStep
      ? `Validar exclusivamente a entrega atual “${activeStep.title}”, sem exigir entregas futuras do pedido completo.`
      : input.objective,
    reviewStage: activeStep ? "local_step" : "final_environment",
    ...(activeStep ? { activeStepId: activeStep.id, currentDeliverable } : {}),
    deferredDeliverables,
    scope: activeStep
      ? `Somente a entrega atual e seus alvos cobertos: ${input.changedElements.map((element) => element.name).join(", ") || activeStep.deliverable}. Entregas posteriores estão explicitamente fora deste laudo.`
      : input.changedElements.length
        ? `Somente os elementos alterados nesta rodada: ${input.changedElements.map((element) => element.name).join(", ")}.`
        : "Somente os elementos efetivamente alterados nesta rodada.",
    hardConstraints: [...new Set(explicitRequirements.map((requirement) => requirement.requirement))],
    explicitRequirements,
    // The original briefing already carries externally requested relations.
    // Repeating a planner-authored spatial contract here would leak its answer.
    spatialIntents: [],
    changedElements: input.changedElements,
    acceptanceCriteria: activeStep ? activeStep.acceptanceCriteria : [],
    reviewHistory: (input.reviewHistory ?? []).slice(0, 24),
    ...(input.environment ? {
      environmentContext: environmentContextForReview(
        input.environment,
        input.plan.assumptions ?? [],
        input.plan.spatialDecisions
          .filter((decision) => decision.datum && (decision.bottomHeightMm != null || decision.topHeightMm != null))
          .map((decision) => ({
            subject: decision.subject,
            kind: decision.datum!.kind,
            ...(decision.datum!.reference ? { reference: decision.datum!.reference } : {}),
            ...(decision.datum!.justification ? { justification: decision.datum!.justification } : {}),
          })),
      ),
    } : {}),
  };
}

/**
 * Returns a clean visual-review conversation containing only the original
 * briefing and exactly one current capture. It never carries execution prose,
 * selected alternatives, previous verdicts or the planner's rationale.
 */
const MAX_FINAL_REVIEW_CAPTURES = 4;

/**
 * Capture pairs (tool_use + tool_result) the critic should see. A local step
 * review judges the latest capture only. The final environment review judges
 * the packet: the panorama capture that opened the verification plus the
 * directed recaptures that completed its coverage — otherwise the critic is
 * asked to approve the whole set while looking at the last close-up alone.
 */
type CapturePair = { call: AgentToolUseBlock; result: AgentToolResultBlock };

function isEnvironmentCapturePair(pair: CapturePair): boolean {
  const result = pair.result.result as Record<string, unknown>;
  const callInput = pair.call.input && typeof pair.call.input === "object" ? pair.call.input as Record<string, unknown> : {};
  return result.context === "environment" || callInput.context === "environment";
}

function captureIdOf(pair: CapturePair): string {
  return String((pair.result.result as Record<string, unknown>).captureId);
}

function capturePairsForReview(
  messages: AgentMessage[],
  review: IndependentReviewBrief,
): CapturePair[] {
  const pairs: CapturePair[] = [];
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    for (const block of messages[messageIndex]!.content) {
      if (block.type !== "tool_result" || !block.result || typeof block.result !== "object") continue;
      const result = block.result as Record<string, unknown>;
      if (typeof result.captureId !== "string") continue;
      let call: AgentToolUseBlock | null = null;
      for (let callIndex = messageIndex; callIndex >= 0 && !call; callIndex -= 1) {
        const match = messages[callIndex]!.content.find((candidate): candidate is AgentToolUseBlock => (
          candidate.type === "tool_use" && candidate.id === block.toolUseId && candidate.name === "scene.viewport.capture"
        ));
        if (match) call = match;
      }
      if (!call) continue;
      const pair: CapturePair = { call, result: block };
      pairs.unshift(pair);
      if (review.reviewStage !== "final_environment" || isEnvironmentCapturePair(pair) || pairs.length >= MAX_FINAL_REVIEW_CAPTURES) {
        return pairs;
      }
    }
  }
  return pairs;
}

export function independentVisualReviewMessages(
  rawMessages: AgentMessage[],
  review: IndependentReviewBrief,
): AgentMessage[] | null {
  const messages = withoutToolNarration(rawMessages);
  const pairs = capturePairsForReview(messages, review);
  if (!pairs.length) return null;
  const environmentPair = pairs.find(isEnvironmentCapturePair);
  const request = review.reviewStage === "local_step"
    ? `${review.originalObjective}\nEntregável atual: ${review.currentDeliverable ?? review.scope}\nNão reprove por qualquer item listado em deferredDeliverables: ele ainda não pertence a esta etapa.`
    : pairs.length > 1
      ? `${review.originalObjective}\nAs capturas a seguir formam um único pacote de revisão: o panorama do ambiente${environmentPair ? ` (captureId ${captureIdOf(environmentPair)})` : ""} e as recapturas dirigidas que completaram a cobertura dos alvos. Registre um único laudo${environmentPair ? ` contra o captureId ${captureIdOf(environmentPair)}, com o bloco environment,` : ""} usando as recapturas como evidência dos alvos que o panorama não mostrou com clareza.`
      : review.originalObjective;
  return ensureToolResultProtocol([
    { role: "user", content: [{ type: "text", text: request }] },
    ...pairs.flatMap((pair): AgentMessage[] => [
      { role: "assistant", content: [pair.call] },
      { role: "user", content: [pair.result] },
    ]),
  ]);
}

function subjectsOverlap(left: string, right: string): boolean {
  const normalize = (value: string): string => value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const a = normalize(left);
  const b = normalize(right);
  return Boolean(a && b && (a.includes(b) || b.includes(a)));
}
